import React, { useReducer, useCallback, useMemo, useEffect } from 'react'
import useMetadata from '../hooks/useMetadata'

export const CheckboxProviderContext = React.createContext({})

const checkboxReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CREATOR_CHECKBOXES': {
      return { ...state, creatorCheckboxes: Object.values(action.value) }
    }
    case 'HANDLE_NEW_DATA': {
      return getInitialCheckboxes(action.value)
    }
    default:
      throw new Error(`Unhandled type in checkboxReducer: ${action.type}`)
  }
}

export default function CheckboxProvider({ children }) {
  const { setCreator, metadata } = useMetadata()
  const [state, dispatch] = useReducer(checkboxReducer, getInitialCheckboxes(metadata))

  useEffect(() => {
    dispatch({ type: 'HANDLE_NEW_DATA', value: metadata })
  }, [metadata])

  const resetCreatorCheckboxes = useCallback(() => {
    dispatch({ type: 'SET_CREATOR_CHECKBOXES', value: Object.keys(metadata.creator).map(() => false) })
  }, [metadata])

  const deleteSelectedCreatorCheckboxes = useCallback(() => {
    const newCreatorKeys = Object.keys(metadata?.creator || {}).filter((_, i) => !state.creatorCheckboxes[i])
    const newCreators = {}
    newCreatorKeys.forEach((key) => {
      newCreators[key] = metadata.creator[key]
    })
    setCreator(newCreators)
  }, [metadata, setCreator, state])

  const deleteMode = useMemo(() => getIsAnyCheckboxChecked(state.creatorCheckboxes), [state.creatorCheckboxes])

  const value = useMemo(
    () => ({
      creatorCheckboxes: state.creatorCheckboxes,
      resets: state.resets,
      dispatch,
      resetCreatorCheckboxes,
      deleteSelectedCreatorCheckboxes,
      deleteMode,
    }),
    [
      state.creatorCheckboxes,
      state.resets,
      dispatch,
      resetCreatorCheckboxes,
      deleteSelectedCreatorCheckboxes,
      deleteMode,
    ]
  )

  return <CheckboxProviderContext.Provider value={value}>{children}</CheckboxProviderContext.Provider>
}

function getInitialCheckboxes(metadata) {
  return {
    creatorCheckboxes: metadata?.creator ? Object.keys(metadata.creator).map(() => false) : [],
    resets: 0,
  }
}

function getIsAnyCheckboxChecked(...checkboxLists) {
  return checkboxLists.some((list) => list.some((checkbox) => Boolean(checkbox)))
}
